/**
 * CompletedStopCard Component
 *
 * Displays a completed or skipped stop in a collapsed/expandable view.
 * Read-only display of items and work order details.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, Badge } from '../ui';
import { salesforceClient } from '../../lib/salesforce';
import { BerichtComposer } from '../berichten/BerichtComposer';
import { isWerkbank } from '../../lib/uiFlag';
import { WbButton, typeLabel } from '../wb';
import { decodeHtmlEntities } from '../../lib/utils';

/** Plaats uit het adres ("… 1782 AB Den Helder" → "Den Helder"); leeg als niet te vinden. */
function plaatsUit(adres: string | undefined): string | null {
  if (!adres) return null;
  const plat = decodeHtmlEntities(adres).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const m = plat.match(/\d{4}\s*[A-Z]{2}\s+(.+)$/i);
  return m?.[1]?.trim() ?? null;
}
import type { CompletedStopCardProps, WasserijItemDto, WorkOrderDetailResponse } from './types';

/** "14:05" uit de afrondtijd; leeg als de stop geen tijdstip heeft. */
function afrondTijd(stop: CompletedStopCardProps['stop']): string | null {
  const raw = (stop as unknown as { Verwerkt_op__c?: string; LastModifiedDate?: string }).Verwerkt_op__c
    ?? (stop as unknown as { LastModifiedDate?: string }).LastModifiedDate;
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' }).format(d);
}

export function CompletedStopCard({ stop, onCall, onHeropen }: CompletedStopCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [items, setItems] = useState<WasserijItemDto[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);

  // Load items when expanded
  useEffect(() => {
    if (!isExpanded || items.length > 0 || !stop.Werk_Order__c) return;

    async function loadItems() {
      setIsLoadingItems(true);
      try {
        const response = await salesforceClient.callApexRest<{ success: boolean; data?: WorkOrderDetailResponse }>(
          `/v1/field/workorders/${stop.Werk_Order__c}`
        );
        if (response.success && response.data?.items) {
          setItems(response.data.items);
        }
      } catch (error) {
        console.error('Failed to load items for completed stop:', error);
      } finally {
        setIsLoadingItems(false);
      }
    }

    loadItems();
  }, [isExpanded, stop.Werk_Order__c, items.length]);

  // Get display values
  const displayAddress = (stop.address || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const displayWorkOrderName = stop.workOrderName || stop.Werk_Order__r?.Name || stop.Name;

  // Items to display: Primary items OR any item with klaarleggen=true (e.g., ondertapijten)
  const displayItems = items.filter(item =>
    (item.productCategory || 'Primary') === 'Primary' || item.klaarleggen === true
  );
  const totalItemCount = displayItems.length;
  const heeftTelefoon = Boolean(stop.phoneNumber || stop.Werk_Order__r?.Mobiel__c || stop.Werk_Order__r?.Telefoon__c);
  const kanBellen = Boolean(onCall && heeftTelefoon);
  const kanHeropenen = Boolean(onHeropen && stop.Overgeslagen__c);

  // Werkbank: één regel van 44 px, "✓ 5 · Naam", rechts het tijdstip mono;
  // tik klapt de bestaande details uit.
  if (isWerkbank()) {
    const tijd = afrondTijd(stop);
    return (
      <>
        <div className="rounded-wb border-wb border-wb-gedempt bg-wb-vlak">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            className="wb-focus w-full min-h-[44px] flex items-center gap-2 px-3 text-left"
          >
            <span className={`w-6 h-6 shrink-0 rounded-[4px] flex items-center justify-center text-white ${stop.Overgeslagen__c ? 'bg-wb-opmerkingrand' : 'bg-wb-inkt'}`}>
              {stop.Overgeslagen__c ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" d="M12 8v4m0 4h.01" /></svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              )}
            </span>
            <span className="font-mono text-sm text-wb-inkt2 shrink-0">{stop.Route_Sequence__c ?? '?'}</span>
            <span className="flex-1 min-w-0 truncate font-semibold text-wb-inkt">
              {stop.customerName}
              {plaatsUit(stop.address) && <span className="font-normal text-wb-inkt2"> · {plaatsUit(stop.address)}</span>}
              {stop.Overgeslagen__c && stop.Reden_Overslaan__c && (
                <span className="font-normal text-wb-inkt2"> · {stop.Reden_Overslaan__c}</span>
              )}
            </span>
            {tijd && <span className="font-mono text-sm text-wb-inkt2 shrink-0">{tijd}</span>}
            <span className={`shrink-0 text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-[4px] text-wb-inkt ${stop.Type__c === 'Ophalen' ? 'bg-wb-ophalen' : 'bg-wb-afleveren'}`}>
              {typeLabel(stop.Type__c)}
            </span>
          </button>

          {isExpanded && (
            <div className="px-3 pb-3 pt-1 border-t-wb border-wb-gedempt space-y-2">
              {displayAddress && <p className="text-sm text-wb-inkt2">{displayAddress}</p>}
              <div className="flex items-center justify-between">
                <span className="font-mono font-semibold text-wb-inkt">{displayWorkOrderName}</span>
                <span className="font-mono text-sm text-wb-inkt2">
                  {stop.totalM2 ? `${stop.totalM2.toFixed(2).replace('.', ',')} m²` : ''}
                  {totalItemCount > 0 ? ` · ${totalItemCount} ${totalItemCount === 1 ? 'item' : 'items'}` : ''}
                </span>
              </div>
              {isLoadingItems ? (
                <p className="text-sm text-wb-inkt2">Items laden…</p>
              ) : items.length > 0 && !stop.Overgeslagen__c ? (
                <ul className="space-y-1">
                  {displayItems.map((item) => (
                    <li key={item.itemId} className="text-sm text-wb-inkt">
                      <span className="font-mono">{item.itemName}</span>
                      <span className="text-wb-inkt2"> · {item.productName || 'Vloerkleed'}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {stop.Overgeslagen__c && stop.Opmerking_Overslaan__c && (
                <p className="text-sm text-wb-inkt2">{stop.Opmerking_Overslaan__c}</p>
              )}
              {(kanBellen || kanHeropenen) && (
                <div className={`grid gap-2 pt-1 ${kanBellen && kanHeropenen ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {kanBellen && (
                    <WbButton variant="secondary" onClick={onCall}>
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                      Bel
                    </WbButton>
                  )}
                  {kanHeropenen && (
                    <WbButton variant="secondary" onClick={onHeropen}>
                      {stop.Type__c === 'Terugbrengen' ? 'Toch afleveren' : 'Toch ophalen'}
                    </WbButton>
                  )}
                </div>
              )}
              <WbButton variant="link" onClick={() => setShowMessageModal(true)}>Bericht aan kantoor</WbButton>
            </div>
          )}
        </div>
        <BerichtComposer
          isOpen={showMessageModal}
          onClose={() => setShowMessageModal(false)}
          onSent={() => setShowMessageModal(false)}
          werkOrder={{
            id: stop.Werk_Order__c || undefined,
            name: displayWorkOrderName,
            customerName: stop.customerName || undefined,
          }}
        />
      </>
    );
  }

  return (
    <>
    <Card className="cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
      <CardContent className="p-md">
        <div className="flex items-center gap-md">
          {/* Checkmark */}
          <div className={`
            w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
            ${stop.Overgeslagen__c ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'}
          `}>
            {stop.Overgeslagen__c ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-xs">
              <span className="text-xs text-kleda-text-muted">#{stop.Route_Sequence__c}</span>
              <span className="font-medium text-kleda-text-main truncate">
                {stop.customerName}
                {stop.contactName && stop.contactName !== stop.customerName && (
                  <span className="text-kleda-text-muted font-normal"> • {stop.contactName}</span>
                )}
              </span>
            </div>
            {stop.Overgeslagen__c && stop.Reden_Overslaan__c && (
              <p className="text-xs text-amber-600">
                Overgeslagen: {stop.Reden_Overslaan__c}
              </p>
            )}
          </div>

          {/* Badge and expand icon */}
          <div className="flex items-center gap-xs">
            <Badge
              variant={stop.Overgeslagen__c ? 'warning' : 'success'}
              size="sm"
            >
              {stop.Overgeslagen__c ? 'Overgeslagen' : 'Voltooid'}
            </Badge>
            <svg
              className={`w-4 h-4 text-kleda-text-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Expanded details (read-only) */}
        {isExpanded && (
          <div className="mt-md pt-md border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
            {/* Address */}
            {displayAddress && (
              <p className="text-sm text-kleda-text-muted mb-sm truncate">{displayAddress}</p>
            )}

            {/* Work order summary */}
            <div className="bg-kleda-bg-clean rounded-kleda-md p-sm mb-sm">
              <div className="flex items-center justify-between mb-xs">
                <span className="font-medium text-kleda-text-main">{displayWorkOrderName}</span>
                {stop.totalInclBtw != null && stop.totalInclBtw > 0 && (
                  <span className="font-bold text-kleda-primary">
                    €{stop.totalInclBtw.toFixed(2).replace('.', ',')}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-md gap-y-xs text-xs text-kleda-text-muted">
                {stop.totalM2 && <span>{stop.totalM2.toFixed(2).replace('.', ',')} m²</span>}
                {totalItemCount > 0 && <span>• {totalItemCount} {totalItemCount === 1 ? 'item' : 'items'}</span>}
              </div>
            </div>

            {/* Items list (read-only) */}
            {isLoadingItems ? (
              <div className="text-center py-md">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-kleda-primary mx-auto"></div>
                <p className="text-xs text-kleda-text-muted mt-sm">Items laden...</p>
              </div>
            ) : items.length > 0 && !stop.Overgeslagen__c ? (
              <div className="space-y-sm">
                <p className="text-xs font-medium text-kleda-text-muted uppercase">
                  {stop.Type__c === 'Ophalen' ? 'Opgehaalde items' : 'Afgeleverde items'}
                </p>
                {displayItems.map(item => {
                  const relatedExtras = items.filter(
                    i => i.parentItemId === item.itemId && i.productCategory === 'Extra'
                  );
                  const relatedPackages = items.filter(
                    i => i.parentItemId === item.itemId && i.productCategory === 'Package'
                  );

                  return (
                    <div key={item.itemId} className="bg-gray-50 rounded-kleda-md p-sm">
                      <div className="flex items-center justify-between mb-xs">
                        <span className={`font-medium text-sm ${
                          item.klaarleggen === false ? 'text-kleda-text-muted' : 'text-kleda-text-main'
                        }`}>
                          {item.productName || 'Vloerkleed'}{' '}
                          <span className={`font-normal ${
                            item.klaarleggen === false ? 'text-kleda-text-muted/70' : 'text-kleda-text-muted'
                          }`}>· {item.itemName}</span>
                        </span>
                        {item.surfaceM2 && item.oppervlakteBerekeningNodig !== false && (
                          <span className="text-xs text-kleda-text-muted">
                            {item.surfaceM2.toFixed(2).replace('.', ',')} m²
                          </span>
                        )}
                      </div>

                      {/* Package and extras tags */}
                      {(relatedPackages.length > 0 || relatedExtras.length > 0) && (
                        <div className="flex flex-wrap gap-xs">
                          {relatedPackages.map(pkg => (
                            <span
                              key={pkg.itemId}
                              className="inline-flex items-center bg-kleda-primary/10 text-kleda-primary text-xs font-medium px-2 py-0.5 rounded"
                            >
                              {pkg.productName}
                            </span>
                          ))}
                          {relatedExtras.map(extra => (
                            <span
                              key={extra.itemId}
                              className="inline-block bg-white border border-gray-300 text-gray-700 text-xs px-2 py-0.5 rounded"
                            >
                              {extra.productName}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : stop.Overgeslagen__c ? null : (
              <p className="text-sm text-kleda-text-muted text-center py-sm">
                Geen items gevonden
              </p>
            )}

            {(kanBellen || kanHeropenen) && (
              <div className="flex gap-sm mt-sm">
                {kanBellen && (
                  <button
                    onClick={onCall}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-kleda-text-main border border-slate-300 rounded-lg hover:border-kleda-primary transition-colors"
                  >
                    Bel klant
                  </button>
                )}
                {kanHeropenen && (
                  <button
                    onClick={onHeropen}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-kleda-primary border border-kleda-primary/40 rounded-lg hover:bg-kleda-primary/5 transition-colors"
                  >
                    {stop.Type__c === 'Terugbrengen' ? 'Toch afleveren' : 'Toch ophalen'}
                  </button>
                )}
              </div>
            )}

            {/* Message button */}
            <div className="flex items-center pt-sm mt-sm border-t border-gray-100">
              <button
                onClick={() => setShowMessageModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 hover:text-kleda-primary border border-slate-200 hover:border-kleda-primary/30 rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Bericht aan kantoor
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Bericht aan kantoor */}
    <BerichtComposer
      isOpen={showMessageModal}
      onClose={() => setShowMessageModal(false)}
      onSent={() => setShowMessageModal(false)}
      werkOrder={{
        id: stop.Werk_Order__c || undefined,
        name: displayWorkOrderName,
        customerName: stop.customerName || undefined,
      }}
    />
    </>
  );
}
